// components/TopNeighborhoodsSection.js
"use client";
import { useState, useEffect } from "react";
import { Segment } from "semantic-ui-react";
import { Title } from "../../../app/_shims/semantic-ui-react";
import { NeighborhoodsList } from "../../../common/index.js";
import { formattedCityName } from "../../../common/Utils";
import { getcitydata } from "../../../modules/Graphql/cityquestionandanswergraphql.js";

export default function TopNeighborhoodsSection({
  nhdetails,
  topneighborhood: initialTopneighborhood,
  isMobile,
  togglePrev: externalTogglePrev,
  activePage: externalActivePage,
  showList = false, // For SSR mode, always show list regardless of isMobile
  isSSR = false,
}) {
  // Internal state for SSR/hybrid mode
  const [internalActivePage, setInternalActivePage] = useState(1);
  const [topneighborhood, setTopneighborhood] = useState(
    initialTopneighborhood || [],
  );
  const [isLoading, setIsLoading] = useState(false);
  const [hasFetched, setHasFetched] = useState(false);

  // Fetch top neighborhoods data from GraphQL
  // Flow:
  // - If isSSR = true (NeighborhoodServer.js) → Show SSR data, then fetch fresh
  // - If isSSR = false (NeighborhoodComponent.js) → Use parent data, don't fetch
  useEffect(() => {
    const fetchTopNeighborhoods = async () => {
      if (isSSR && nhdetails?.[0]?.cityfullname && !hasFetched) {
        if (!initialTopneighborhood?.length) {
          setIsLoading(true);
        }

        try {
          const getdata = {
            start: 0,
            limit: 100,
            order: "DESC",
            orderBy: "npi",
            fields: [
              {
                name: "cityfullname",
                value: nhdetails[0].cityfullname,
                operator: "=",
              },
            ],
          };

          const result = await getcitydata(getdata);
          // Update with fresh data from API
          if (result?.data?.getcitydata && result.data.getcitydata.length > 0) {
            setTopneighborhood(result.data.getcitydata);
          }
          setHasFetched(true); // Mark as fetched to prevent duplicate calls
        } catch (error) {
          console.error("Error fetching top neighborhoods:", error);
          setHasFetched(true);
        } finally {
          setIsLoading(false);
        }
      }
    };

    fetchTopNeighborhoods();
  }, [nhdetails?.[0]?.cityfullname, isSSR]);

  useEffect(() => {
    if (!isSSR && initialTopneighborhood && initialTopneighborhood.length > 0) {
      setTopneighborhood(initialTopneighborhood);
    }
  }, [initialTopneighborhood, isSSR]);

  // Internal handler for pagination
  const internalTogglePrev = (e, { value }, id) => {
    setInternalActivePage(value.activePage);
  };

  // Determine which props to use (external if provided, otherwise internal)
  const isExternallyControlled =
    externalTogglePrev && externalActivePage !== undefined;
  const activePage = isExternallyControlled
    ? externalActivePage
    : internalActivePage;
  const togglePrev = isExternallyControlled
    ? externalTogglePrev
    : internalTogglePrev;
  const cityFullname = nhdetails?.[0]?.cityfullname || "";
  const labelSuffix = nhdetails?.[0]?.label
    ? nhdetails[0].label.split(",").pop().trim()
    : "";

  const hasNeighborhoods = topneighborhood && topneighborhood?.length > 0;
  const firstNeighborhood = hasNeighborhoods ? topneighborhood[0] : null;

  // Don't render if no data and not loading
  if (!hasNeighborhoods && !isLoading) {
    return null;
  }

  return (
    <>
      <Segment
        textAlign="left"
        basic
        style={{ padding: " 13px 1em", margin: "0px" }}
      >
        <div className="top_nh_title">
          <Title
            size="medium"
            className="top_nh_txt_right"
            as="h2"
            style={{ float: "left", margin: "7px 0", letterSpacing: "0.3px" }}
          >
            Top Neighborhoods in {"  "}
          <span className="" style={{ float: "none", marginLeft: "3px" }}>
            {isLoading ? (
              <span style={{ fontStyle: "italic", color: "#666" }}>
                Loading...
              </span>
            ) : (
              <a
                href={`/Nhranking/best-overall/${cityFullname}`}
                target="_blank"
              >
                {firstNeighborhood ? (
                  <>
                    {firstNeighborhood.census_name}
                    {firstNeighborhood.label ? `, ${firstNeighborhood.label.split(",").pop()}` : ""}
                  </>
                ) : (
                  ""
                )}
              </a>
            )}
          </span>
          </Title>
        </div>
      </Segment>

      {(isMobile || showList) && !isLoading && (
        <NeighborhoodsList
          id="neighborhoods"
          itemData={hasNeighborhoods ? topneighborhood : []}
          prev={togglePrev}
          active={activePage}
          href="/Neighborhood/"
          customclass="sim_nei_imgs"
          ispage="detailcity"
          isEnabled={true}
          seeFullListLink={`/Nhranking/best-overall/${formattedCityName(
            firstNeighborhood?.postalcity_name || "",
          )}-${labelSuffix}`}
        />
      )}
    </>
  );
}
